import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark } from 'lucide-react';
import { cn } from '@/utils/cn';
import { IconButton } from '@/components/atoms/IconButton';
import storyStyles from '@/pages/student/StoryPage/StoryPage.module.scss';
import type { VocabularyFlashcardProps } from './VocabularyFlashcard.types';

/**
 * Misma marca y clases que vocabulary_card en ActivitiesTab (historias).
 */
export function VocabularyFlashcard({
  item,
  isSaved = false,
  onToggleSave,
  className,
}: VocabularyFlashcardProps) {
  const { t } = useTranslation();
  const [wordAudioPlaying, setWordAudioPlaying] = useState(false);
  const wordAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    wordAudioRef.current = null;
    setWordAudioPlaying(false);
    return () => {
      if (wordAudioRef.current) {
        wordAudioRef.current.pause();
        wordAudioRef.current.onended = null;
        wordAudioRef.current = null;
      }
    };
  }, [item.wordAudioUrl]);

  const handleWordPlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.wordAudioUrl) return;
    if (!wordAudioRef.current) {
      wordAudioRef.current = new Audio(item.wordAudioUrl);
      wordAudioRef.current.onended = () => setWordAudioPlaying(false);
    }
    if (wordAudioPlaying) {
      wordAudioRef.current.pause();
      wordAudioRef.current.currentTime = 0;
      setWordAudioPlaying(false);
    } else {
      void wordAudioRef.current.play();
      setWordAudioPlaying(true);
    }
  };

  const handleSaveToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSave?.(item);
  };

  const PlayButton = item.wordAudioUrl ? (
    <button
      type="button"
      className={storyStyles.story__vocabCardPlayBtn}
      onClick={handleWordPlay}
      aria-label={wordAudioPlaying ? t('activities.pausePronunciation') : t('activities.playPronunciation')}
    >
      {wordAudioPlaying ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>
  ) : null;

  return (
    <div className={cn(storyStyles.story__vocabCard, className)}>
      {onToggleSave && (
        <IconButton
          icon={Bookmark}
          variant="bare"
          size="md"
          active={isSaved}
          className={storyStyles.story__vocabCardSaveBtn}
          onClick={handleSaveToggle}
          aria-pressed={isSaved}
          aria-label={
            isSaved ? t('activities.removeVocabAria', { word: item.word }) : t('activities.saveVocabAria', { word: item.word })
          }
        />
      )}
      {item.wordAudioUrl && (
        <div className={storyStyles.story__vocabCardPlayWrap}>{PlayButton}</div>
      )}
      <div className={storyStyles.story__vocabCardWordRow}>
        <span className={storyStyles.story__vocabCardWord}>{item.word}</span>
        <span className={storyStyles.story__vocabCardPos}>{item.partOfSpeech}</span>
      </div>
      <p className={storyStyles.story__vocabCardDefinition}>{item.definitionEn}</p>
      <p className={storyStyles.story__vocabCardExample}>&quot;{item.exampleSentence}&quot;</p>
      {item.commonMistakes && (
        <div className={storyStyles.story__vocabCardMistakes}>
          <span className={storyStyles.story__vocabCardMistakesLabel}>{t('activities.commonMistake')}</span>{' '}
          {item.commonMistakes}
        </div>
      )}
      {item.relatedWords.length > 0 && (
        <div className={storyStyles.story__vocabCardRelated}>
          <span className={storyStyles.story__vocabCardRelatedLabel}>{t('activities.related')}</span>
          {item.relatedWords.map((rw) => (
            <span key={rw} className={storyStyles.story__vocabCardRelatedTag}>
              {rw}
            </span>
          ))}
        </div>
      )}
      {item.tags.length > 0 && (
        <div className={storyStyles.story__vocabCardRelated}>
          <span className={storyStyles.story__vocabCardRelatedLabel}>{t('activities.tags')}</span>
          {item.tags.map((t) => (
            <span key={t} className={storyStyles.story__vocabCardRelatedTag}>
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

VocabularyFlashcard.displayName = 'VocabularyFlashcard';
